"use client";

import { type PointerEvent, useCallback, useRef } from "react";

export type CreatorVideoTimelineProps = {
  videoWidth: number;
  progressPercent: number;
  onSeek?: (percent: number) => void;
};

/**
 * Shows an interactive progress track aligned to the player width.
 * Supports click-to-seek and drag-to-scrub via pointer events.
 */
export const CreatorVideoTimeline = ({
  videoWidth,
  progressPercent,
  onSeek,
}: CreatorVideoTimelineProps) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);

  const clampedPercent = Number.isFinite(progressPercent)
    ? Math.min(100, Math.max(0, progressPercent))
    : 0;

  const getPercentFromPointer = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) {
      return null;
    }
    const rect = track.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.min(100, Math.max(0, (x / rect.width) * 100));
  }, []);

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!onSeek) {
        return;
      }
      event.preventDefault();
      isDraggingRef.current = true;
      (event.target as HTMLElement).setPointerCapture(event.pointerId);

      const percent = getPercentFromPointer(event.clientX);
      if (percent !== null) {
        onSeek(percent);
      }
    },
    [onSeek, getPercentFromPointer],
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current || !onSeek) {
        return;
      }
      event.preventDefault();
      const percent = getPercentFromPointer(event.clientX);
      if (percent !== null) {
        onSeek(percent);
      }
    },
    [onSeek, getPercentFromPointer],
  );

  const onPointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    isDraggingRef.current = false;
  }, []);

  return (
    <div
      className="absolute -bottom-2 left-0 z-50 flex w-full justify-center px-4"
      style={{ opacity: videoWidth > 0 ? 1 : 0 }}
    >
      {/* Enlarged hit area for easier touch interaction */}
      <div
        ref={trackRef}
        className="relative flex cursor-pointer items-center py-2"
        style={{ width: videoWidth > 0 ? `${videoWidth}px` : "100%" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="slider"
        aria-label="Video timeline"
        aria-valuenow={Math.round(clampedPercent)}
        aria-valuemin={0}
        aria-valuemax={100}
        tabIndex={0}
      >
        {/* Visible track */}
        <div className="relative h-1.5 w-full rounded-md bg-neutral-700">
          <div
            className="bg-primary h-full rounded-md"
            style={{ width: `${clampedPercent}%` }}
          />
          {/* Thumb */}
          <div
            className="absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 rounded-xs bg-white shadow-sm"
            style={{ left: `${clampedPercent}%` }}
          />
        </div>
      </div>
    </div>
  );
};
